import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
// REMOVIDO: import supabase - usar APENAS useAuth
import '../../styles/login-page.css';

const LoginPage = () => {
  const { login, user, resetPassword } = useAuth(); // Usar resetPassword do hook
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validated, setValidated] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: ''
  });

  // Função utilitária para verificar se o usuário é admin
  const isUserAdmin = (userData) => {
    if (!userData) return false;
    
    // Verificar múltiplas propriedades possíveis
    const adminChecks = [
      userData.type === 'admin',
      userData.user_type === 'admin',
      userData.role === 'admin',
      userData.userType === 'admin',
      userData.perfil === 'admin',
      userData.profile === 'admin',
      
      // Verificar em maiúsculas também
      userData.type?.toLowerCase() === 'admin',
      userData.user_type?.toLowerCase() === 'admin',
      userData.role?.toLowerCase() === 'admin',
      userData.userType?.toLowerCase() === 'admin',
      userData.perfil?.toLowerCase() === 'admin',
      userData.profile?.toLowerCase() === 'admin',
      
      // Verificar se tem alguma propriedade que indique admin
      userData.isAdmin === true,
      userData.is_admin === true,
      userData.admin === true,
      
      // Verificar arrays de roles
      Array.isArray(userData.roles) && userData.roles.includes('admin'),
      Array.isArray(userData.permissions) && userData.permissions.includes('admin'),
    ];
    
    console.log('🔍 Verificações de admin:', {
      userData,
      checks: adminChecks,
      result: adminChecks.some(check => check === true)
    });
    
    return adminChecks.some(check => check === true);
  };

  // Função para redirecionar baseado no tipo de usuário
  const redirectUserAfterLogin = (userData) => {
    console.log('🚀 Redirecionando usuário após login:', userData);
    
    if (isUserAdmin(userData)) {
      console.log('✅ Usuário identificado como ADMIN, indo para /admin/orders');
      navigate('/admin/orders');
    } else {
      console.log('✅ Usuário identificado como EXPOSITOR, indo para /expositor-home');
      navigate('/expositor-home');
    }
  };
  
  // Redirecionar se já estiver logado
  useEffect(() => {
    if (user) {
      console.log('👤 Usuário já logado, redirecionando...');
      console.log('🔍 Dados do usuário:', user);
      redirectUserAfterLogin(user);
    }
  }, [user, navigate]);

  // Funções de validação
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!email) {
      return 'O email é obrigatório';
    }
    if (!emailRegex.test(email)) {
      return 'Por favor, insira um email válido';
    }
    return '';
  };

  const validatePassword = (password) => {
    if (!password) {
      return 'A senha é obrigatória';
    }
    return '';
  };

  // Função de sanitização
  const sanitizeInput = (input) => {
    return input
      .replace(/[<>]/g, '')
      .replace(/['"]/g, '')
      .replace(/\\/g, '')
      .trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação de inputs
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    
    setValidationErrors({
      email: emailError,
      password: passwordError
    });
    
    if (emailError || passwordError) {
      setValidated(true);
      return;
    }
    
    setError(null);
    setLoading(true);
    
    // Sanitize inputs antes de enviar
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = password;
    
    try {
      console.log('🔐 Iniciando processo de login para:', sanitizedEmail);
      
      // USAR APENAS O HOOK useAuth - sem importação direta do supabase
      const userData = await login(sanitizedEmail, sanitizedPassword);
      
      console.log('✅ Login realizado com sucesso');
      console.log('🔍 Dados completos do usuário:', userData);
      
      // Redirecionar baseado no tipo de usuário usando a nova função
      redirectUserAfterLogin(userData);
      
    } catch (error) {
      console.error('💥 Erro de login:', error);
      
      // Tratar diferentes tipos de erro do Supabase
      let errorMessage = 'Erro ao fazer login. Tente novamente.';
      
      if (error.message?.includes('Invalid login credentials') || 
          error.message?.includes('invalid') ||
          error.message?.includes('credentials')) {
        errorMessage = 'Credenciais inválidas. Por favor, verifique seu email e senha.';
      } else if (error.message?.includes('não possui acesso válido')) {
        errorMessage = 'Usuário não possui acesso válido ao sistema. Entre em contato com o administrador.';
      } else if (error.message?.includes('Usuário não encontrado ou inativo')) {
        errorMessage = 'Usuário não encontrado ou inativo. Entre em contato com o administrador.';
      } else if (error.message?.includes('Erro ao verificar permissões')) {
        errorMessage = 'Erro ao verificar permissões do usuário. Tente novamente.';
      } else if (error.message?.includes('Email not confirmed')) {
        errorMessage = 'Email não confirmado. Verifique sua caixa de entrada.';
      } else if (error.message?.includes('Too many requests')) {
        errorMessage = 'Muitas tentativas de login. Aguarde alguns minutos antes de tentar novamente.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Por favor, insira um email válido para recuperar sua senha');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // USAR resetPassword do hook useAuth ao invés de supabase direto
      await resetPassword(email);
      
      setError(null);
      alert('✅ Enviamos um link para redefinir sua senha. Verifique seu email.');
      
    } catch (error) {
      console.error('Erro ao solicitar recuperação de senha:', error);
      setError('Não foi possível processar sua solicitação. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-logo-container">
          <div className="login-logo">
            <img src="/logo-mm-eventos.png" alt="MM Eventos®" />
          </div>
          <div className="brand-text">Portal de Expositores</div>
        </div>
        
        <div className="login-form-container">
          <div className="login-form-content">
            <h1>Bem-vindo</h1>
            <p className="login-subtitle">Portal para Expositores</p>
            
            {error && (
              <Alert 
                variant="danger" 
                className="login-alert"
              >
                {error}
              </Alert>
            )}
            
            <Form className="login-form" onSubmit={handleSubmit}>
              <Form.Group className="login-form-group">
                <Form.Control 
                  type="email" 
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setValidationErrors(prev => ({ ...prev, email: '' }));
                    if (error) setError(null); // Limpar erro ao digitar
                  }}
                  className={`login-input ${validationErrors.email ? 'is-invalid' : ''}`}
                  disabled={loading}
                />
                {validationErrors.email && (
                  <div className="login-validation-feedback">
                    {validationErrors.email}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="login-form-group">
                <Form.Control 
                  type="password" 
                  placeholder="Senha"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setValidationErrors(prev => ({ ...prev, password: '' }));
                    if (error) setError(null); // Limpar erro ao digitar
                  }}
                  className={`login-input ${validationErrors.password ? 'is-invalid' : ''}`}
                  disabled={loading}
                />
                {validationErrors.password && (
                  <div className="login-validation-feedback">
                    {validationErrors.password}
                  </div>
                )}
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="login-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Entrando...
                  </>
                ) : 'Entrar'}
              </Button>
            </Form>
          </div>
        </div>
        
        <div className="login-footer">
          <p>© {new Date().getFullYear()} MM Eventos® Health Division</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;